import { useEffect, useMemo } from "react"
import { DateTime } from "luxon"
import { useField, useFormikContext } from "formik"
import { DateTimePicker } from "@mui/x-date-pickers"

import { DeliveryTimesField, DynamicConfigField, DynamicConfigKey, MeetingServiceTabValues } from "./formTypes"
import { createInitEvents, DEFAULT_DURATION_MINUTES } from "../resource/resourceBookingCalendars/helpers"
import { NullableCalendarEvent } from "../resource/calendar/fullCalendarTypes"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { DynamicFormFieldGroupWrap } from "./DynamicFormField"
import { getFormikFieldError } from "./helpers"
import { roundToNearest5Minutes } from "../helpers"

type DynamicDateTimeProps = {
    config: DynamicConfigField
}

export function DynamicDateTime({ config }: DynamicDateTimeProps) {
    const field = config.inputField

    if (!field || !config.active) return null

    const dateTimeFactory = useDateTime()

    const label = (config.label || config.description || "") + (field.isRequired ? " *" : "")
    const now = dateTimeFactory.now()

    const { submitCount } = useFormikContext<MeetingServiceTabValues>()

    const [{ value: event }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [{ value: currentStart }, startMeta, startHelpers] = useField<DateTime | null>(
        DynamicConfigKey.MEETING_START_TIME,
    )
    const [{ value: currentEnd }, endMeta, endHelpers] = useField<DateTime | null>(DynamicConfigKey.MEETING_END_TIME)
    const [{ value: multipleDeliveries }] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const eventStart = event?.start ? dateTimeFactory.fromISO(event.start) : null
    const eventEnd = event?.end ? dateTimeFactory.fromISO(event.end) : null

    const deliveryDate = multipleDeliveries?.deliveryDate
    const deliveryTime = multipleDeliveries?.deliveryTimes?.[0]?.time

    const deliveryDateTime =
        deliveryDate && deliveryTime ? dateTimeFactory.fromISO(`${deliveryDate}T${deliveryTime}`) : null

    const fallbackStartTime = useMemo(() => {
        if (!currentStart && eventStart) return eventStart
        if (deliveryDateTime && deliveryDateTime > now) return deliveryDateTime
        return now
    }, [currentStart, eventStart, deliveryDateTime, now])

    useEffect(() => {
        if (config.key !== DynamicConfigKey.MEETING_START_TIME) return

        const shouldInitFromEvent =
            !!eventStart && !!eventEnd && (!currentStart?.equals?.(eventStart) || !currentEnd?.equals?.(eventEnd))

        const shouldInitFromDelivery =
            !eventStart &&
            !eventEnd &&
            multipleDeliveries?.activeTime &&
            deliveryDateTime &&
            (!currentStart || !currentEnd)

        if (shouldInitFromEvent || shouldInitFromDelivery) {
            const roundedStart = roundToNearest5Minutes(shouldInitFromEvent ? eventStart : fallbackStartTime)

            const roundedEnd = shouldInitFromEvent
                ? eventEnd!
                : roundedStart.plus({ minutes: DEFAULT_DURATION_MINUTES })

            startHelpers.setValue(roundedStart)
            endHelpers.setValue(roundedEnd)

            if (!eventStart || !eventEnd) {
                eventHelpers.setValue(
                    createInitEvents(
                        [],
                        dateTimeFactory.toApiISOMinutesFormat(roundedStart),
                        dateTimeFactory.toApiISOMinutesFormat(roundedEnd),
                    ),
                )
            }
        }
    }, [
        config.key,
        multipleDeliveries?.activeTime,
        deliveryDateTime?.toISO(),
        eventStart?.toISO(),
        eventEnd?.toISO(),
        currentStart?.toISO?.(),
        currentEnd?.toISO?.(),
    ])

    if (config.key === DynamicConfigKey.MEETING_START_TIME) {
        const errorProps = getFormikFieldError(startMeta, submitCount)
        return (
            <DynamicFormFieldGroupWrap config={config}>
                <DateTimePicker
                    value={currentStart}
                    label={label}
                    onChange={(value: DateTime | null) => {
                        if (!value) {
                            startHelpers.setValue(null)
                            return
                        }
                        const roundedStart = roundToNearest5Minutes(value)
                        startHelpers.setValue(roundedStart)

                        const roundedStartValue = dateTimeFactory.toApiISOMinutesFormat(roundedStart)

                        if (!currentEnd || roundedStart > currentEnd) {
                            const roundedEnd = roundedStart.plus({ minutes: DEFAULT_DURATION_MINUTES })
                            endHelpers.setValue(roundedEnd)
                            eventHelpers.setValue(
                                createInitEvents(
                                    [],
                                    roundedStartValue,
                                    dateTimeFactory.toApiISOMinutesFormat(roundedEnd),
                                ),
                            )
                        } else
                            eventHelpers.setValue(
                                createInitEvents(
                                    [],
                                    roundedStartValue,
                                    dateTimeFactory.toApiISOMinutesFormat(currentEnd),
                                ),
                            )
                    }}
                    minDateTime={eventStart ? now : fallbackStartTime}
                    slotProps={{ textField: { fullWidth: true, size: "small", ...errorProps } }}
                    disabled={!!event?.resourceIds.length}
                />
            </DynamicFormFieldGroupWrap>
        )
    }

    if (config.key === DynamicConfigKey.MEETING_END_TIME) {
        const errorProps = getFormikFieldError(endMeta, submitCount)
        const minEndTime = currentStart ? currentStart.plus({ minutes: 5 }) : now.plus({ minutes: 5 })

        return (
            <DynamicFormFieldGroupWrap config={config}>
                <DateTimePicker
                    value={currentEnd}
                    label={label}
                    minDateTime={minEndTime}
                    onChange={(value) => {
                        if (!value) {
                            endHelpers.setValue(null)
                            return
                        }
                        const roundedEnd = roundToNearest5Minutes(value)
                        endHelpers.setValue(roundedEnd)

                        if (!currentStart) return
                        eventHelpers.setValue(
                            createInitEvents(
                                [],
                                dateTimeFactory.toApiISOMinutesFormat(currentStart),
                                dateTimeFactory.toApiISOMinutesFormat(roundedEnd),
                            ),
                        )
                    }}
                    slotProps={{ textField: { fullWidth: true, size: "small", ...errorProps } }}
                    disabled={!!event?.resourceIds.length}
                />
            </DynamicFormFieldGroupWrap>
        )
    }

    return null
}
